import * as moment from 'moment';
import { Transaction } from '../abstract/transaction.model';
import { Bank } from '../config/bank.model';
import { ChequeRefusalReason } from './cheque-refusal-reason.model';
import { CurrencyPipe } from '@angular/common';
import { StatusEnum } from '../../../enums/status.enum';
import { tagAccents } from 'src/assets/themes/biifor/components/tag';
import { ChequeAllocation } from './cheque-allocation.model';

export class Cheque extends Transaction {

    static override CLASS_NAME: string = 'Cheque';

    private _bank!: Bank;
    private _expectedCashDate!: moment.Moment;
    private _number!: string;
    private _refusalReason!: ChequeRefusalReason;

    /*----------------------------------------------------------------------------------------
     * Méthodes statiques
     ----------------------------------------------------------------------------------------*/

    public static override statusSeverity(status: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
        switch (true) {
            case (status === StatusEnum.Cancelled):
                return 'secondary';
            case (status === StatusEnum.Cashed):
            case (status === StatusEnum.Deposited):
                return 'success';
            case (status === StatusEnum.AwaitingCorrection):
            case (status === StatusEnum.AwaitingRegularization):
            case (status === StatusEnum.New):
                return 'warn';
            case (status === StatusEnum.Refused):
                return 'danger';
            default:
                return super.statusSeverity(status);
        }
    }

    public static override statusLabel(status: string): string {
        switch (true) {
            case (status === StatusEnum.AwaitingCorrection):
                return 'Attente correction';
            case (status === StatusEnum.AwaitingRegularization):
                return 'Attente régularisation';
            case (status === StatusEnum.Cancelled):
                return 'Annulé';
            case (status === StatusEnum.Cashed):
                return 'Encaissé';
            case (status === StatusEnum.Deposited):
                return 'Déposé';
            case (status === StatusEnum.Refused):
                return 'Rejeté';
            default:
                return super.statusLabel(status);
        }
    }

    public static override tagAccent(status: string): Object | undefined {
        switch (true) {
            case (status === StatusEnum.Cashed):
            case (status === StatusEnum.AwaitingCorrection):
            case (status === StatusEnum.AwaitingRegularization):
                return tagAccents;
            default:
                return undefined;
        }
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    /**
     * Le tiers du chèque dépend de son type : un partenaire, un associé, ou aucun
     * (« Liste plan comptable »). Le libellé commence par celui qui existe.
     */
    public override toString(withAmount: boolean = false): string {
        let parts: string[] = [];
        if (this.partner) {
            parts.push(this.partner.name);
        } else if (this.shareholder) {
            parts.push(this.shareholder.fullName);
        }
        parts.push(this.transactionDate.format('DD/MM/YYYY'));
        if (this.bank) {
            parts.push(this.bank.commercialName);
        }
        if (this.number && this.number.length > 0) {
            parts.push(this.number);
        }

        if (withAmount) {
            let pipe = new CurrencyPipe('fr');
            parts.push(pipe.transform(this.amount, this.currency.code, 'code')!);
        }

        return parts.join(' - ');
    }

    /*----------------------------------------------------------------------------------------
     * Accesseurs
     ----------------------------------------------------------------------------------------*/

    public get allocations(): ChequeAllocation[] {
        return this._allocations as ChequeAllocation[];
    }

    public set allocations(value: any) {
        this._setValue('_allocations', value, 'ChequeAllocation');
    }

    public get bank(): Bank {
        return this._bank;
    }

    public set bank(value: any) {
        this._setValue('_bank', value, 'Bank');
    }

    public get expectedCashDate(): moment.Moment {
        return this._expectedCashDate;
    }

    public set expectedCashDate(value: any) {
        this._setValue('_expectedCashDate', value, 'date');
    }

    public get number(): string {
        return this._number;
    }

    public set number(value: any) {
        this._setValue('_number', value, 'string');
    }

    public get refusalReason(): ChequeRefusalReason {
        return this._refusalReason;
    }

    public set refusalReason(value: any) {
        this._setValue('_refusalReason', value, 'ChequeRefusalReason');
    }

    /**
     * Les statuts sont testés par ordre de priorité
     */
    public override get statusSeverity(): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
        switch (true) {
            case (this.hasStatus(StatusEnum.AwaitingCorrection)):
                return Cheque.statusSeverity(StatusEnum.AwaitingCorrection);
            case (this.hasStatus(StatusEnum.AwaitingRegularization)):
                return Cheque.statusSeverity(StatusEnum.AwaitingRegularization);
            case (this.hasStatus(StatusEnum.Cancelled)):
                return Cheque.statusSeverity(StatusEnum.Cancelled);
            case (this.hasStatus(StatusEnum.Cashed)):
                return Cheque.statusSeverity(StatusEnum.Cashed);
            case (this.hasStatus(StatusEnum.Deposited)):
                return Cheque.statusSeverity(StatusEnum.Deposited);
            case (this.hasStatus(StatusEnum.Refused)):
                return Cheque.statusSeverity(StatusEnum.Refused);
            default:
                return super.statusSeverity;
        }
    }

    /**
     * Les statuts sont testés par ordre de priorité
     */
    public override get statusLabel(): string {
        switch (true) {
            case (this.hasStatus(StatusEnum.AwaitingCorrection)):
                return Cheque.statusLabel(StatusEnum.AwaitingCorrection);
            case (this.hasStatus(StatusEnum.AwaitingRegularization)):
                return Cheque.statusLabel(StatusEnum.AwaitingRegularization);
            case (this.hasStatus(StatusEnum.Cancelled)):
                return Cheque.statusLabel(StatusEnum.Cancelled);
            case (this.hasStatus(StatusEnum.Cashed)):
                return Cheque.statusLabel(StatusEnum.Cashed);
            case (this.hasStatus(StatusEnum.Deposited)):
                return Cheque.statusLabel(StatusEnum.Deposited);
            case (this.hasStatus(StatusEnum.Refused)):
                return Cheque.statusLabel(StatusEnum.Refused);
            default:
                return super.statusLabel;
        }
    }

    public get tagAccent(): Object | undefined {
        switch (true) {
            case (this.hasStatus(StatusEnum.AwaitingCorrection)):
                return Cheque.tagAccent(StatusEnum.AwaitingCorrection);
            case (this.hasStatus(StatusEnum.AwaitingRegularization)):
                return Cheque.tagAccent(StatusEnum.AwaitingRegularization);
            case (this.hasStatus(StatusEnum.Cancelled)):
                return Cheque.tagAccent(StatusEnum.Cancelled);
            case (this.hasStatus(StatusEnum.Cashed)):
                return Cheque.tagAccent(StatusEnum.Cashed);
            case (this.hasStatus(StatusEnum.Deposited)):
                return Cheque.tagAccent(StatusEnum.Deposited);
            case (this.hasStatus(StatusEnum.New)):
                return Cheque.tagAccent(StatusEnum.New);
            case (this.hasStatus(StatusEnum.Validated)):
                return Cheque.tagAccent(StatusEnum.Validated);
            case (this.hasStatus(StatusEnum.Refused)):
                return Cheque.tagAccent(StatusEnum.Refused);
            default:
                return Cheque.tagAccent('');
        }
    }

}
